import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Search, Sparkles, FileText, Users, CircleDot } from "lucide-react";
import { useLocaleFormat } from "@/lib/i18n-format";
import { useFilterByOwnerCompany } from "@/owner/OwnerCompanyContext";
import { CompanyBadge } from "@/owner/CompanyBadge";
import { cleanChangeOrderTitle } from "@/lib/change-order-text";

export const Route = createFileRoute("/_app/change-orders/")({
  component: ChangeOrdersListPage,
});

type Status = "draft" | "sent" | "approved" | "rejected";

type Row = {
  id: string;
  number: string;
  title: string;
  status: Status;
  total: number;
  currency: string;
  created_at: string;
  company_id: string;
  estimate_id: string | null;
  estimates: {
    number: string;
    title: string;
    client_id: string | null;
    clients: { name: string } | null;
  } | null;
};

const statusVariant: Record<Status, "default" | "secondary" | "destructive" | "outline"> = {
  draft: "secondary",
  sent: "outline",
  approved: "default",
  rejected: "destructive",
};

function ChangeOrdersListPage() {
  const { t } = useTranslation();
  const { formatMoney } = useLocaleFormat();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [clientFilter, setClientFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const statusOptions: Status[] = ["draft", "sent", "approved", "rejected"];

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("change_orders")
        .select(
          "id, number, title, status, total, currency, created_at, company_id, estimate_id, estimates(number, title, client_id, clients(name))",
        )
        .order("created_at", { ascending: false });
      if (error) {
        toast.error(t("pages.common.loadFailed"), { description: error.message });
      } else {
        setRows((data ?? []) as unknown as Row[]);
      }
      setLoading(false);
    })();
  }, [t]);

  const scoped = useFilterByOwnerCompany(rows);

  const clientOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of scoped) {
      const cid = r.estimates?.client_id;
      const name = r.estimates?.clients?.name;
      if (cid && name) map.set(cid, name);
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [scoped]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = scoped;
    if (clientFilter !== "all") {
      list = list.filter((r) => r.estimates?.client_id === clientFilter);
    }
    if (statusFilter !== "all") {
      list = list.filter((r) => r.status === statusFilter);
    }
    if (!q) return list;
    return list.filter((r) =>
      [r.number, r.title, r.estimates?.clients?.name, r.estimates?.number]
        .filter(Boolean)
        .some((v) => v!.toLowerCase().includes(q)),
    );
  }, [scoped, search, clientFilter, statusFilter]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("pages.changeOrders.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("pages.changeOrders.subtitle")}</p>
        </div>
        <Button onClick={() => navigate({ to: "/change-orders/new" })} className="gap-2">
          <Plus className="h-4 w-4" />
          {t("pages.changeOrders.newButton")}
        </Button>
      </div>

      <div className="rounded-lg border border-border bg-card">
        <div className="flex flex-col gap-2 border-b border-border p-3 sm:flex-row sm:items-center">
          <div className="flex flex-1 items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("pages.common.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 border-0 bg-transparent shadow-none focus-visible:ring-0"
            />
          </div>
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger className="h-8 w-full sm:w-[200px]">
              <div className="flex items-center gap-2">
                <Users className="h-3.5 w-3.5 text-muted-foreground" />
                <SelectValue placeholder={t("pages.changeOrders.headers.client")} />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("common.all")}</SelectItem>
              {clientOptions.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 w-full sm:w-[160px]">
              <div className="flex items-center gap-2">
                <CircleDot className="h-3.5 w-3.5 text-muted-foreground" />
                <SelectValue placeholder={t("pages.changeOrders.headers.status")} />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("common.all")}</SelectItem>
              {statusOptions.map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`statuses.${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Badge variant="secondary" className="sm:ml-2">
            {t("pages.changeOrders.count", { count: filtered.length })}
          </Badge>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 p-12 text-center">
            <div
              className="flex h-12 w-12 items-center justify-center rounded-full text-primary-foreground"
              style={{ background: "var(--gradient-brand)" }}
            >
              <Sparkles className="h-6 w-6" />
            </div>
            <div>
              <p className="font-medium">
                {rows.length === 0
                  ? t("pages.changeOrders.empty")
                  : t("pages.changeOrders.noMatch")}
              </p>
              <p className="text-sm text-muted-foreground">
                {rows.length === 0
                  ? t("pages.changeOrders.emptyDesc")
                  : t("pages.common.tryDifferentSearch")}
              </p>
            </div>
            {rows.length === 0 && (
              <Button onClick={() => navigate({ to: "/change-orders/new" })} className="gap-2">
                <Plus className="h-4 w-4" />
                {t("pages.changeOrders.newButton")}
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("pages.changeOrders.headers.number")}</TableHead>
                  <TableHead>{t("pages.changeOrders.headers.client")}</TableHead>
                  <TableHead>{t("pages.changeOrders.headers.title")}</TableHead>
                  <TableHead>{t("pages.changeOrders.headers.estimate")}</TableHead>
                  <TableHead>{t("pages.changeOrders.headers.status")}</TableHead>
                  <TableHead className="text-right">
                    {t("pages.changeOrders.headers.total")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.id} className="cursor-pointer">
                    <TableCell className="font-mono text-xs">
                      <Link
                        to="/change-orders/$changeOrderId"
                        params={{ changeOrderId: r.id }}
                        className="hover:underline"
                      >
                        {r.number}
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <span>{r.estimates?.clients?.name ?? "—"}</span>
                        <CompanyBadge companyId={r.company_id} />
                      </div>
                    </TableCell>
                    <TableCell>
                      <Link
                        to="/change-orders/$changeOrderId"
                        params={{ changeOrderId: r.id }}
                        className="flex items-center gap-2 font-medium hover:underline"
                      >
                        <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                        {cleanChangeOrderTitle(r.title)}
                      </Link>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {r.estimates?.number ?? "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusVariant[r.status] ?? "outline"}>
                        {t(`statuses.${r.status}`)}
                      </Badge>
                    </TableCell>
                    <TableCell
                      className={`text-right font-semibold ${
                        r.total >= 0 ? "text-emerald-600" : "text-rose-600"
                      }`}
                    >
                      {r.total >= 0 ? "+" : ""}
                      {formatMoney(r.total, r.currency)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
